import type { ComputedRef, Ref } from 'vue'
import type { User } from '@/types/auth'
import type {
  ParticipantRegisterPayload,
  ParticipantLoginPayload,
  UpdateParticipantProfilePayload,
} from '@/types/registration'
import {
  registerParticipant as apiRegisterParticipant,
  loginParticipant as apiLoginParticipant,
  updateParticipantProfile as apiUpdateParticipantProfile,
  logoutParticipant as apiLogoutParticipant,
} from '@/api/registration'
import { useAuth } from '@/composables/useAuth'

// Participant auth uses the same underlying user ref as organizer auth.
// A single Sanctum session serves both zones — whoever logs in (organizer or
// participant) is the same person, so there is no need for a separate ref.

async function registerParticipant(payload: ParticipantRegisterPayload): Promise<User> {
  const { user } = useAuth()
  const newUser = await apiRegisterParticipant(payload)
  user.value = newUser
  return newUser
}

async function loginParticipant(payload: ParticipantLoginPayload): Promise<User> {
  const { user } = useAuth()
  const loggedUser = await apiLoginParticipant(payload)
  user.value = loggedUser
  return loggedUser
}

async function updateParticipantProfile(payload: UpdateParticipantProfilePayload): Promise<User> {
  const { user } = useAuth()
  const updatedUser = await apiUpdateParticipantProfile(payload)
  user.value = updatedUser
  return updatedUser
}

async function logoutParticipant(): Promise<void> {
  const { user } = useAuth()
  await apiLogoutParticipant()
  user.value = null
}

function setParticipantUser(newUser: User | null): void {
  const { user } = useAuth()
  user.value = newUser
}

async function initParticipantAuth(): Promise<User | null> {
  // The router guard (router/index.ts) already resolves the shared user
  // state before a guarded navigation completes. This function is kept for
  // call-site compatibility but is a no-op.
  const { user } = useAuth()
  return user.value
}

export function useParticipantAuth(): {
  participantUser: Ref<User | null>
  isParticipantAuthenticated: ComputedRef<boolean>
  registerParticipant: (payload: ParticipantRegisterPayload) => Promise<User>
  loginParticipant: (payload: ParticipantLoginPayload) => Promise<User>
  updateParticipantProfile: (payload: UpdateParticipantProfilePayload) => Promise<User>
  logoutParticipant: () => Promise<void>
  setParticipantUser: (user: User | null) => void
  initParticipantAuth: () => Promise<User | null>
} {
  const { user, isAuthenticated } = useAuth()

  return {
    participantUser: user,
    isParticipantAuthenticated: isAuthenticated,
    registerParticipant,
    loginParticipant,
    updateParticipantProfile,
    logoutParticipant,
    setParticipantUser,
    initParticipantAuth,
  }
}
